import { useEffect, useMemo, useState } from 'react';
import api from '../utils/axiosConfig';
import { computeRunningWorkoutEditorSummary } from '../utils/runningWorkoutEditorSummary';
import { resolveEffectiveThresholdPaceSecPerKm } from '../utils/thresholdPaceResolve';

/**
 * Shared across all hook instances — catalog grids can render many running cards; without this,
 * each card duplicated /running-cp-baseline + /users/settings and hung the UI on search.
 */
let baselinePayloadCache = null;
let baselineInflightPromise = null;

function loadBaselinePayloadOnce() {
  if (baselinePayloadCache) return Promise.resolve(baselinePayloadCache);
  if (!baselineInflightPromise) {
    baselineInflightPromise = Promise.all([
      api.get('/api/training/running-cp-baseline').catch(() => ({ data: {} })),
      api.get('/api/users/settings').catch(() => ({ data: {} })),
      api.get('/api/strava/thresholds').catch(() => ({ data: {} })),
    ]).then(([bRes, sRes, thRes]) => {
      baselinePayloadCache = { bRes, sRes, thRes };
      return baselinePayloadCache;
    });
  }
  return baselineInflightPromise;
}

/** Call after user updates running CP / distance units in settings so the next mount refetches. */
export function invalidateRunningCpPreviewCache() {
  baselinePayloadCache = null;
  baselineInflightPromise = null;
}

/**
 * Loads athlete CP baseline + settings and computes duration/distance/weighted %CP
 * from split rows (same math as planner / graphic editor summaries).
 */
export function useRunningWorkoutCpPreview(splits, t) {
  const [baseline, setBaseline] = useState(null);
  const [cpWatts, setCpWatts] = useState(null);
  const [distanceUnits, setDistanceUnits] = useState('km');

  useEffect(() => {
    let cancelled = false;
    loadBaselinePayloadOnce().then((payload) => {
      if (cancelled || !payload) return;
      const { bRes, sRes, thRes } = payload;
      setBaseline({ ...(bRes.data || {}), thresholds: thRes?.data || {} });
      const s = sRes.data || {};
      if (s.runningCriticalPowerWatts != null && Number.isFinite(Number(s.runningCriticalPowerWatts))) {
        setCpWatts(Number(s.runningCriticalPowerWatts));
      } else if (bRes.data?.cpWatts != null) {
        setCpWatts(Number(bRes.data.cpWatts));
      } else {
        setCpWatts(null);
      }
      setDistanceUnits(s.distanceUnits === 'miles' ? 'miles' : 'km');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const thresholdPaceSecPerKm = useMemo(() => {
    const th = baseline?.thresholds;
    const fromWeekly = resolveEffectiveThresholdPaceSecPerKm({
      trainingThresholdPaceSecPerKm: th?.trainingThresholdPace,
      ceilingThresholdPaceSecPerKm: th?.ceilingThresholdPace,
    });
    if (fromWeekly != null) return fromWeekly;
    if (th?.thresholdPace != null && th.thresholdPace > 0) return Number(th.thresholdPace);
    const paceAtCp = baseline?.paceSecPerKmAtCp;
    if (paceAtCp != null && Number(paceAtCp) > 0) return Number(paceAtCp);
    return null;
  }, [baseline]);

  const thresholdBpm = useMemo(() => {
    const bpm = baseline?.thresholds?.thresholdBpm;
    if (bpm != null && Number.isFinite(Number(bpm)) && Number(bpm) > 0) return Number(bpm);
    return null;
  }, [baseline]);

  const powerPaceModel = baseline?.powerPaceModel ?? null;
  const powerHrModel = baseline?.powerHrModel ?? null;
  const raceHrProfile = baseline?.raceHrProfile ?? { anchors: [], sampleCount: 0 };

  const summary = useMemo(() => {
    const segs = Array.isArray(splits) ? splits : [];
    if (segs.length === 0 || typeof t !== 'function') {
      return {
        distanceLabel: '—',
        timeLabel: '—',
        sufferScoreLabel: '—',
        weightedCpLabel: '—',
        totalMeters: 0,
        totalSec: 0,
        weightedCp: null,
        sufferScoreEstimate: null,
      };
    }
    return computeRunningWorkoutEditorSummary(
      segs,
      thresholdPaceSecPerKm,
      distanceUnits,
      t,
      { cpWatts, powerPaceModel }
    );
  }, [splits, thresholdPaceSecPerKm, distanceUnits, t, cpWatts, powerPaceModel]);

  const paceEstimateOpts = useMemo(
    () => ({ cpWatts, powerPaceModel }),
    [cpWatts, powerPaceModel]
  );

  const hrEstimateOpts = useMemo(
    () => ({ cpWatts, powerHrModel, raceHrProfile }),
    [cpWatts, powerHrModel, raceHrProfile]
  );

  return {
    baseline,
    cpWatts,
    thresholdPaceSecPerKm,
    thresholdBpm,
    powerPaceModel,
    powerHrModel,
    raceHrProfile,
    paceEstimateOpts,
    hrEstimateOpts,
    distanceUnits,
    summary,
  };
}
